import { Box, styled } from '@mui/material';
import useRawMaterials from 'components/ComponentsLibrary/hooks/useRawMaterials';
import { useDialog } from 'components/hooks';
import { IGeoUploadFormValues, geoUploadDatasetSchema } from 'constants/schemas/geoUpload.schema';
import { ThemeButton } from 'designSystem';
import GeoDataItem from 'designSystem/DataDisplay/GeoDataItem/GeoDataItem';
import Icon from 'designSystem/Primitives/Icon/Icon';
import { useFormikContext } from 'formik';
import React, { FC } from 'react';
import { v4 as uuid } from 'uuid';

interface IDatasetFormikTabsProps {
  selectedIndex: number;
  onChange: (newSelectedIndex: number) => void;
  /**
   * Id of the dataset to delete
   * If undefined, the dataset was not created yet, but documents might exist that need to be deleted
   */
  onDeleteDataset: (dataset: { id: string | undefined; documents: { id: string }[] }) => void;
}

const GeoDataContainer = styled('div')(() => ({
  cursor: 'pointer',
}));

const DatasetFormikTabs: FC<IDatasetFormikTabsProps> = ({
  selectedIndex,
  onChange,
  onDeleteDataset,
}) => {
  const { values, errors, touched, setValues, setFieldTouched } =
    useFormikContext<IGeoUploadFormValues>();
  const { rawMaterials } = useRawMaterials();
  const { openDialog } = useDialog();

  const handleAddDatasetClick = () => {
    // Add new dataset to the form
    const newValues = [...values, geoUploadDatasetSchema.default()];
    setValues([...values, geoUploadDatasetSchema.default()]);
    // Select the new dataset
    onChange(newValues.length - 1);
  };

  const handleTabChange = (index: number) => {
    onChange(index);
    setFieldTouched(`${selectedIndex}`, true, true);
  };

  const handleRemoveDataset = (indexToRemove: number) => {
    if (values.length < 2) {
      return; // Do not allow removing the last dataset
    }
    onChange(0);
    // Make sure to delete the dataset on the backend if it exists
    const datasetIdToDelete = values[indexToRemove].datasetId;
    const documentIdsToDelete = values[indexToRemove].files;
    if (datasetIdToDelete || documentIdsToDelete) {
      onDeleteDataset({ id: datasetIdToDelete, documents: documentIdsToDelete });
    }
    setValues(values.filter((_, index) => index !== indexToRemove));
  };

  const getDatasetItemTitle = (index: number) => {
    const title = values[index]?.title;
    if (title) {
      return title;
    }
    return `Dataset ${index + 1}`;
  };

  const getRawMaterialTitle = (rawMaterialId?: string) => {
    if (!rawMaterialId) {
      return undefined;
    }
    const rawMaterial = rawMaterials.find(({ id }) => id === rawMaterialId);
    if (rawMaterial) {
      return rawMaterial.title;
    }
    return undefined;
  };

  const handleTabRemove = (index: number) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Remove dataset',
        itemTitle: getDatasetItemTitle(index),
        text: 'Are you sure you want to remove this dataset? This will delete all the data and files associated with this dataset.',
        submitText: 'Remove',
        cancelText: 'Cancel',
        onCancel: () => undefined,
        onSubmit: () => handleRemoveDataset(index),
      },
    });
  };

  return (
    <Box display="flex" mt={2} gap={2} flexWrap="wrap">
      {values.map(({ datasetId, countryCode, rawMaterialId, ownedBy }, index) => (
        <GeoDataContainer key={datasetId || uuid()} onClick={() => handleTabChange(index)}>
          <GeoDataItem
            title={getDatasetItemTitle(index)}
            commodity={getRawMaterialTitle(rawMaterialId)}
            countryCode={countryCode}
            owner={ownedBy?.name}
            active={selectedIndex === index}
            status={touched[index] && errors[index] ? 'error' : undefined}
            onClickDelete={values.length < 2 ? undefined : () => handleTabRemove(index)}
          />
        </GeoDataContainer>
      ))}
      <ThemeButton
        variant="text"
        color="WHITE"
        startIcon={<Icon name="plus" />}
        onClick={handleAddDatasetClick}
      >
        Add dataset
      </ThemeButton>
    </Box>
  );
};

export default DatasetFormikTabs;
